/**
 * Offerings Management Tab
 * Phase 2: Class & Webinar Schedules
 * 
 * IMPORTANT: METADATA ONLY - NO ENROLLMENT TRACKING
 * Links to external registration_url only.
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useOfferingInstances,
  useCreateOffering,
  useUpdateOfferingStatus,
  useDeleteOffering,
  type TrainingOfferingInstance,
} from '@/hooks/useOfferingInstances';
import { useRecommendedTraining } from '@/hooks/useLearningCenter';
import { useTrainingProviders } from '@/hooks/useTrainingProviders';
import {
  Calendar,
  Plus,
  ExternalLink,
  Loader2,
  MapPin,
  Video,
  Building,
  XCircle,
  CheckCircle,
  Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, formatDistanceToNow, isPast } from 'date-fns';
import { cn } from '@/lib/utils';

const STATUS_COLORS: Record<string, string> = {
  upcoming: 'bg-blue-100 text-blue-800',
  ongoing: 'bg-green-100 text-green-800',
  completed: 'bg-gray-100 text-gray-800',
  cancelled: 'bg-red-100 text-red-800',
};

const DELIVERY_ICONS: Record<string, React.ReactNode> = {
  online: <Video className="h-4 w-4" />,
  in_person: <Building className="h-4 w-4" />,
  hybrid: <MapPin className="h-4 w-4" />,
};

export function OfferingsManagementTab() {
  const [viewMode, setViewMode] = useState<'available' | 'history' | 'all'>('available');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const statusIn = viewMode === 'available' 
    ? ['upcoming', 'ongoing'] 
    : viewMode === 'history' 
      ? ['completed', 'cancelled'] 
      : undefined;

  const { data: offerings, isLoading } = useOfferingInstances({ statusIn });
  const updateStatus = useUpdateOfferingStatus();
  const deleteOffering = useDeleteOffering();

  const handleUpdateStatus = async (offering: TrainingOfferingInstance, status: TrainingOfferingInstance['status']) => {
    try {
      await updateStatus.mutateAsync({ offeringId: offering.id, status });
      toast.success(`Offering marked as ${status}`);
    } catch (error) {
      toast.error('Failed to update offering');
    }
  };

  const handleDelete = async (offering: TrainingOfferingInstance) => {
    try {
      await deleteOffering.mutateAsync(offering.id);
      toast.success('Offering deleted');
    } catch (error) {
      toast.error('Failed to delete offering');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Training Offerings
              </CardTitle>
              <CardDescription>
                Scheduled classes, webinars, and live training sessions
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 rounded-lg border p-1">
                <Button
                  variant={viewMode === 'available' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('available')}
                >
                  Available
                </Button>
                <Button
                  variant={viewMode === 'history' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('history')}
                >
                  History
                </Button>
                <Button
                  variant={viewMode === 'all' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('all')}
                >
                  All
                </Button>
              </div>
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Offering
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!offerings || offerings.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No training offerings scheduled</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => setCreateDialogOpen(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add First Offering
              </Button>
            </div>
          ) : (
            <ScrollArea className="h-[400px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Training</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Format</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {offerings.map((offering) => (
                    <TableRow key={offering.id}>
                      <TableCell>
                        <div className="font-medium truncate max-w-[200px]">
                          {(offering.artifact as any)?.title || 'Unknown'}
                        </div>
                        {offering.venue_name && (
                          <div className="text-xs text-muted-foreground">
                            {offering.venue_name}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {format(new Date(offering.start_datetime), 'MMM d, yyyy')}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {format(new Date(offering.start_datetime), 'h:mm a')}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {DELIVERY_ICONS[offering.delivery_type]}
                          <span className="text-sm capitalize">
                            {offering.delivery_type.replace('_', ' ')}
                          </span>
                        </div>
                        {offering.city && (
                          <div className="text-xs text-muted-foreground">
                            {offering.city}, {offering.state}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="secondary"
                          className={cn('text-xs', STATUS_COLORS[offering.status])}
                        >
                          {offering.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            asChild
                          >
                            <a href={offering.registration_url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </Button>
                          {offering.status === 'upcoming' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleUpdateStatus(offering, 'cancelled')}
                            >
                              <XCircle className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                          {offering.status === 'ongoing' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleUpdateStatus(offering, 'completed')}
                            >
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            </Button>
                          )}
                          {(offering.status === 'completed' || offering.status === 'cancelled') && (
                            <Badge variant="outline" className="text-xs text-muted-foreground">
                              {offering.status === 'completed' ? 'Past' : 'Cancelled'}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Create Offering Dialog */}
      <CreateOfferingDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />
    </>
  );
}

function CreateOfferingDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const createOffering = useCreateOffering();
  const { data: artifactsResult } = useRecommendedTraining({ pageSize: 500 });
  const artifacts = artifactsResult?.data;
  const { data: providers } = useTrainingProviders();

  const [formData, setFormData] = useState({
    artifact_id: '',
    provider_id: '',
    start_datetime: '',
    end_datetime: '',
    timezone: 'America/Chicago',
    delivery_type: 'online' as 'online' | 'in_person' | 'hybrid',
    city: '',
    state: '',
    venue_name: '',
    registration_url: '',
    is_paid: false,
    price_text: '',
    capacity_text: '',
  });

  const handleSubmit = async () => {
    if (!formData.artifact_id || !formData.start_datetime || !formData.registration_url) {
      toast.error('Training, start date, and registration URL are required');
      return;
    }

    try {
      await createOffering.mutateAsync({
        artifact_id: formData.artifact_id,
        provider_id: formData.provider_id || (artifacts?.find(a => a.id === formData.artifact_id) as any)?.provider_id || '',
        start_datetime: new Date(formData.start_datetime).toISOString(),
        end_datetime: formData.end_datetime ? new Date(formData.end_datetime).toISOString() : undefined,
        timezone: formData.timezone,
        delivery_type: formData.delivery_type,
        city: formData.city || undefined,
        state: formData.state || undefined,
        venue_name: formData.venue_name || undefined,
        registration_url: formData.registration_url,
        is_paid: formData.is_paid,
        price_text: formData.price_text || undefined,
        capacity_text: formData.capacity_text || undefined,
      });
      toast.success('Offering created');
      onOpenChange(false);
      // Reset form
      setFormData({
        artifact_id: '',
        provider_id: '',
        start_datetime: '',
        end_datetime: '',
        timezone: 'America/Chicago',
        delivery_type: 'online',
        city: '',
        state: '',
        venue_name: '',
        registration_url: '',
        is_paid: false,
        price_text: '',
        capacity_text: '',
      });
    } catch (error) {
      toast.error('Failed to create offering');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Training Offering</DialogTitle>
          <DialogDescription>
            Schedule a live class, webinar, or in-person training session
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Training Artifact *</Label>
            <Select
              value={formData.artifact_id}
              onValueChange={(v) => setFormData({ ...formData, artifact_id: v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select training" />
              </SelectTrigger>
              <SelectContent>
                {artifacts?.map((artifact) => (
                  <SelectItem key={artifact.id} value={artifact.id}>
                    {artifact.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Date/Time *</Label>
              <Input
                type="datetime-local"
                value={formData.start_datetime}
                onChange={(e) => setFormData({ ...formData, start_datetime: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>End Date/Time</Label>
              <Input
                type="datetime-local"
                value={formData.end_datetime}
                onChange={(e) => setFormData({ ...formData, end_datetime: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Delivery Type</Label>
              <Select
                value={formData.delivery_type}
                onValueChange={(v) => setFormData({ ...formData, delivery_type: v as any })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="online">Online</SelectItem>
                  <SelectItem value="in_person">In-Person</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Timezone</Label>
              <Select
                value={formData.timezone}
                onValueChange={(v) => setFormData({ ...formData, timezone: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="America/New_York">Eastern</SelectItem>
                  <SelectItem value="America/Chicago">Central</SelectItem>
                  <SelectItem value="America/Denver">Mountain</SelectItem>
                  <SelectItem value="America/Los_Angeles">Pacific</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {formData.delivery_type !== 'online' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>City</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="e.g., Houston"
                />
              </div>
              <div className="space-y-2">
                <Label>State</Label>
                <Input
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  placeholder="e.g., TX"
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label>Registration URL *</Label>
            <Input
              value={formData.registration_url}
              onChange={(e) => setFormData({ ...formData, registration_url: e.target.value })}
              placeholder="https://..."
            />
          </div>

          <div className="space-y-2">
            <Label>Capacity (text)</Label>
            <Input
              value={formData.capacity_text}
              onChange={(e) => setFormData({ ...formData, capacity_text: e.target.value })}
              placeholder="e.g., Limited to 25 attendees"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={createOffering.isPending}>
            {createOffering.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Create Offering
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default OfferingsManagementTab;
